import { useState, useMemo, useCallback, useEffect } from "react";

// Component Imports
import {
  Button,
  DropDownInput,
  InputField,
  OptionGroup,
  RadioInput,
  DateInput,
  MiniTabs,
  DataTable,
} from "@/components";

// Icons
import { RiFileExcel2Line } from "react-icons/ri";
import { BsBoxes, BsFiletypeXls, BsShop } from "react-icons/bs";
import { FiX } from "react-icons/fi";
import { MdOutlineDocumentScanner } from "react-icons/md";
// Data & Utilities
import ColumnHelper from "@/utils/ColumnHelper";
import {
  useFilter,
  useApi,
  useAlert,
  useConfirmationDialog,
  usePersistentFilters,
} from "@/";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchShipmentData,
  selectAllShipments,
  cancelShipment,
} from "../../app/slices";
import { subDays } from "date-fns";
import { BiSolidPrinter } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { PDFDocument } from "pdf-lib";

const Tracking = () => {
  /* ----------------------- State Definitions ----------------------- */

  const params = new URLSearchParams(window.location.search);
  const awbsParams = params.get("awbs");
  const orderIdParams = params.get("orderId");
  const { alert, AlertDialog } = useAlert();
  const { confirm, ConfirmationModal } = useConfirmationDialog();

  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;

  const { callApi } = useApi();
  // Filters state
  const [filters, setFilters] = usePersistentFilters({
    orderId: orderIdParams || "",
    courierName: "",
    channelName: "",
    paymentMode: "",
    serviceMode: "",
    awbs: awbsParams || "",
    shippingId: "",
    customerName: "",
    customerPhone: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const navigate = useNavigate();
  // Applied filters for triggering filtering
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Active tab state (for MiniTabs)
  const [activeTab, setActiveTab] = useState("all");

  // Selected rows for DataTable
  const [selectedRows, setSelectedRows] = useState([]);

  // Reset page counter for DataTable pagination
  const [resetPageCounter, setResetPageCounter] = useState(0);

  const dispatch = useDispatch();
  const courier =
    useSelector((state) => {
      const courierData = state.app?.appData?.courier || [];
      if (courierData.length > 0) {
        // Store in sessionStorage when we have data
        sessionStorage.setItem("courierData", JSON.stringify(courierData));
        return courierData;
      }
      // Retrieve from sessionStorage if redux store is empty
      const storedCourier = sessionStorage.getItem("courierData");
      return storedCourier ? JSON.parse(storedCourier) : [];
    }) || [];
  const channel = useSelector((state) => state.app?.appData?.channels) || [];
  const fetchData = useCallback(async () => {
    try {
      const response = await dispatch(
        fetchShipmentData({
          startDate: appliedFilters.date.from,
          endDate: appliedFilters.date.to,
        }),
      );
      if (fetchShipmentData.fulfilled.match(response)) {
        console.log("Shipment data fetched successfully :- ", response.payload);
      } else if (fetchShipmentData.rejected.match(response)) {
        console.log("Error fetching shipment data :-", response.payload);
      }
    } catch (error) {
      console.error("Error in fetchData:", error);
    }
  }, [dispatch, appliedFilters.date.from, appliedFilters.date.to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const shipments = useSelector(selectAllShipments) || [];

  // Filter configuration for useFilteredData
  const filterConfig = useMemo(
    () => ({
      orderId: {
        type: "comma-api",
        itemKey: "userOrderId",
        model: "Shipment",
        endpoint: "/common/search",
      },
      courierName: { type: "partial", itemKey: "courierId" },
      channelName: { type: "exact", itemKey: "order.channel" },
      customerName: {
        type: "partial",
        itemKey: "deliveryAddress.customerName",
      },
      customerPhone: { type: "partial", itemKey: "deliveryPhones.phoneNumber" },
      awbs: {
        type: "comma-api",
        itemKey: "awbNumber",
        model: "Shipment",
        endpoint: "/common/search",
      },
      shippingId: {
        type: "comma",
        itemKey: "shipmentId",
      },
      paymentMode: { type: "exact", itemKey: "order.paymentMode" },
      serviceMode: { type: "exact", itemKey: "serviceMode" },
      date: { type: "date", itemKey: "shipmentDate" },
    }),
    [],
  );

  // Tab filter configuration as a serializable object.
  const tabConfig = useMemo(
    () => ({
      idKey: "status",
      allTabId: "all",
      filters: {
        pendingPickup: { type: "exact", value: "Pending" },
        inTransit: { type: "exact", value: "In Transit" },
        exception: { type: "exact", value: "Exception" },
        outForDelivery: { type: "exact", value: "Out for Delivery" },
        delivered: { type: "exact", value: "Delivered" },
        cancelled: { type: "exact", value: "Cancelled" },
        lost: { type: "exact", value: "Lost" },
        damaged: { type: "exact", value: "Damaged" },
        rtoAll: { type: "exact", value: "RTO" },
        rtoInTransit: { type: "exact", value: "RTO In Transit" },
        rtoDelivered: { type: "exact", value: "RTO Delivered" },
        rtoLost: { type: "exact", value: "RTO Lost" },
      },
    }),
    [],
  );

  // Apply filtering using useFilteredData hook.
  const {
    data: finalFilteredData,
    counts,
    isLoading,
  } = useFilter(shipments, appliedFilters, filterConfig, activeTab, tabConfig);

  /* ----------------------- Memoized Values ----------------------- */
  // Channel Options for DropDownInput

  const channelOptions = useMemo(
    () => channel.map((c) => ({ value: c.name, label: c.name })),
    [channel],
  );

  // Courier Options for DropDownInput
  const courierOptions = useMemo(
    () => courier.map((c) => ({ value: c._id, label: c.name })),
    [courier], // Added courier dependency to refresh when courier data changes
  );

  // Payment Options for RadioInput
  const paymentOptions = useMemo(
    () => [
      { value: "Prepaid", label: "Prepaid" },
      { value: "COD", label: "COD" },
    ],
    [],
  );
  const serviceMode = useMemo(
    () => [
      { value: "Air", label: "Air" },
      { value: "Surface", label: "Surface" },
    ],
    [],
  );

  // Bulk Actions for OptionGroup
  const options = useMemo(
    () => [
      {
        label: "Bulk Label",
        value: "bulk_label",
        icon: <MdOutlineDocumentScanner />,
      },
      { label: "Bulk Invoice", value: "bulk_invoice", icon: <BsFiletypeXls /> },
      { label: "Manifest", value: "manifest", icon: <BsBoxes /> },
      { label: "Cancel", value: "cancel", icon: <FiX /> },
    ],
    [],
  );

  // Tabs for MiniTabs (static counts for now)
  const tabs = useMemo(
    () => [
      { id: "all", label: "All", count: counts.all || 0 },
      {
        id: "pendingPickup",
        label: "Pending Pickup",
        count: counts.pendingPickup || 0,
      },
      { id: "inTransit", label: "In Transit", count: counts.inTransit || 0 },
      {
        id: "exception",
        label: "Exception",
        count: counts.exception || 0,
      },
      {
        id: "outForDelivery",
        label: "Out for Delivery",
        count: counts.outForDelivery || 0,
      },
      { id: "delivered", label: "Delivered", count: counts.delivered || 0 },
      { id: "cancelled", label: "Cancelled", count: counts.cancelled || 0 },
      { id: "lost", label: "Lost", count: counts.lost || 0 },
      { id: "damaged", label: "Damaged", count: counts.damaged || 0 },
      { id: "rtoAll", label: "RTO All", count: counts.rtoAll || 0 },
      {
        id: "rtoInTransit",
        label: "RTO In Transit",
        count: counts.rtoInTransit || 0,
      },
      {
        id: "rtoDelivered",
        label: "RTO Delivered",
        count: counts.rtoDelivered || 0,
      },
      { id: "rtoLost", label: "RTO Lost", count: counts.rtoLost || 0 },
    ],
    [counts],
  );
  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }, []);

  const statusStyle = {
    Pending: "bg-yellow-100/90 text-yellow-800",
    Pickup: "bg-yellow-100/90 text-yellow-800",
    "In Transit": "bg-orange-100 text-orange-800",
    Rescheduled: "bg-orange-100 text-orange-800",
    "Out for Delivery": "bg-purple-200/80 text-purple-800",
    Delivered: "bg-green-100 text-green-800",
    "Partially Delivered": "bg-green-100 text-green-800",
    Cancelled: "bg-cyan-800/20 text-cyan-800",
    Lost: "bg-slate-900/20 text-slate-900",
    Damaged: "bg-amber-900/20 text-amber-900",
    "RTO All": "bg-red-300/30 text-red-800",
    Exception: "bg-red-300/30 text-red-800",
    RTO: "bg-red-300/30 text-red-800",
    "RTO In-Transit": "bg-orange-100 text-orange-800",
    "RTO In Transit": "bg-orange-100 text-orange-800",
    "RTO Delivered": "bg-green-100 text-green-800",
    "RTO Lost": "bg-slate-900/20 text-slate-900",
  };
  const handleView = useCallback((order) => {
    console.log("View Shipment:", order);
    navigate(`orderDetails`, { state: { orderData: order } });
  }, []);
  const handleSearch = (type, value) => {
    if (!value) return; // guard clause to avoid undefined or null
    window.open(
      `https://www.indiancouriercompany.com/tracking?${type}=${value}`,
      "_blank",
    );
    // window.open(`http://localhost:5173/tracking?${type}=${value}`, '_blank');
  };
  // Columns for DataTable
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Channel",
        accessorKey: "order.channel",
        meta: {
          tooltip: (row) => row.order?.channel,
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "userOrderId",
        cell: (info) => (
          <span
            onClick={() => handleView(info.row.original)}
            className="underline text-blue-600 cursor-pointer"
          >
            {info.row.original.userOrderId}
          </span>
        ),
        meta: {
          tooltip: (row) => row.userOrderId,
        },
      }),
      ColumnHelper({
        header: "Booking Date",
        accessorKey: "shipmentDate",
        cell: (info) => formatDate(info.row.original.shipmentDate),
        meta: {
          tooltip: (row) => formatDate(row.shipmentDate),
        },
      }),
      ColumnHelper({
        header: "Shipping ID",
        accessorKey: "shipmentId",
        cell: (info) => {
          const shipmentId = info.row.original.shipmentId;
          return (
            <span
              onClick={() => handleSearch("shipmentId", shipmentId)}
              className="underline text-blue-600 cursor-pointer"
            >
              {shipmentId || "N/A"}
            </span>
          );
        },
        meta: {
          tooltip: (row) => row.shipmentId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Products",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.order?.products
            .map((product) => product.productName)
            .join(", ");
          return <span>{products}</span>;
        },
        meta: {
          tooltip: (row) =>
            row.order?.products.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "payment",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.order?.totalOrderValue)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.order?.totalOrderValue),
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "order.paymentMode",
        meta: {
          tooltip: (row) => row.order?.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Service Mode",
        accessorKey: "serviceMode",
        meta: {
          tooltip: (row) => row.serviceMode,
        },
      }),
      ColumnHelper({
        header: "Customer",
        accessorKey: "deliveryAddress.customerName",
        meta: {
          tooltip: (row) => row.deliveryAddress.customerName,
        },
      }),
      ColumnHelper({
        header: "Customer Phone",
        accessorKey: "deliveryPhones.0.phoneNumber",
        meta: {
          tooltip: (row) => row.deliveryPhones[0].phoneNumber,
        },
      }),
      ColumnHelper({
        header: "Courier",
        accessorKey: "courierId",
        cell: (info) =>
          courier.find((c) => c._id === info.row.original.courierId)?.name,
        meta: {
          tooltip: (row) => courier.find((c) => c._id === row.courierId)?.name,
        },
      }),
      ColumnHelper({
        header: "AWB",
        accessorKey: "awbNumber",
        meta: {
          tooltip: (row) => row.awbNumber,
        },
      }),
      ColumnHelper({
        header: "Weight",
        accessorKey: "order.physicalWeight",
        cell: (info) => `${info.row.original.order?.physicalWeight} kg`,
        meta: {
          tooltip: (row) => `${row.order?.physicalWeight} kg`,
        },
      }),
      ColumnHelper({
        header: "Status",
        accessorKey: "status",
        cell: (info) => (
          <span
            className={`px-2 py-1 rounded-md text-xs font-medium leading-3 ${
              statusStyle[info.row.original.status]
            }`}
          >
            {info.row.original.status}
          </span>
        ),
        meta: {
          tooltip: (row) => row.status,
        },
      }),
    ],
    [],
  );

  /* ----------------------- Handlers ----------------------- */
  // Update filter state
  const handleFilterChange = (field, value) => {
    setFilters({
      [field]: value,
    });
  };

  // Apply filters
  const applyFilters = () => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  };

  // Reset all filters
  const resetFilters = () => {
    const initialFilters = {
      orderId: "",
      courierName: "",
      channelName: "",
      paymentMode: "",
      awbs: "",
      shippingId: "",
      customerName: "",
      customerPhone: "",
      serviceMode: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  };

  async function mergePDFs(urls) {
    // 1) Create a base PDF container
    const mergedPdf = await PDFDocument.create();

    for (const url of urls) {
      // 2) Fetch signed URL content
      const res = await fetch(url);
      const arrayBuffer = await res.arrayBuffer();

      // 3) Load individual PDF
      const pdf = await PDFDocument.load(arrayBuffer);

      // 4) Copy pages into merged PDF
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    // 5) Convert merged PDF to Uint8Array
    const mergedPdfBytes = await mergedPdf.save();

    // 6) Trigger download (browser)
    const blob = new Blob([mergedPdfBytes], { type: "application/pdf" });
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `Amazon_Labels_${new Date().toISOString()}.pdf`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    }, 2000);
  }

  const downloadFile = async (endpoint, ids, type, isAmazon = false) => {
    const loadingIndicator = document.createElement("div");
    loadingIndicator.textContent = `Downloading ${type}...`;
    loadingIndicator.style.position = "fixed";
    loadingIndicator.style.bottom = "20px";
    loadingIndicator.style.right = "20px";
    loadingIndicator.style.backgroundColor = "#4CAF50";
    loadingIndicator.style.color = "white";
    loadingIndicator.style.padding = "10px";
    loadingIndicator.style.borderRadius = "5px";
    loadingIndicator.style.zIndex = "1000";
    document.body.appendChild(loadingIndicator);
    try {
      const response = await callApi(
        endpoint,
        "POST",
        { shippingIds: ids },
        null,
        { Accept: isAmazon ? "application/json" : "application/pdf" },
        isAmazon ? "json" : "blob",
      );

      if (!response || response.size === 0) {
        console.error("Empty response received");
        alert(`Failed to download ${type}. Please try again.`);
        document.body.removeChild(loadingIndicator);
        return;
      }
      if (isAmazon) {
        // response is JSON, so response.data should be an array of URLs
        if (!response.data || response.data.length === 0) {
          console.error("Empty Amazon response received");
          alert(`Failed to download ${type}. Please try again.`);
          document.body.removeChild(loadingIndicator);
          return;
        }

        await mergePDFs(response.data); // pass array of URLs
        setTimeout(() => {
          document.body.removeChild(loadingIndicator);
        }, 2000);
      } else {
        const blob = new Blob([response], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download =
          selectedRows.length === 1
            ? `${type}-${selectedRows[0].shipmentId}.pdf`
            : `${type}-${selectedRows[0].shipmentId}-to-${
                selectedRows[selectedRows.length - 1].shipmentId
              }.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          document.body.removeChild(loadingIndicator);
        }, 2000);
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      alert(
        `Failed to download ${type}. Some labels may be expired or corrupted (Probably Amazon)`,
      );
      document.body.removeChild(loadingIndicator);
    }
  };

  const handleTabChange = useCallback((tab) => {
    console.log("Tab Changed:", tab);
    setActiveTab(tab);
  }, []);

  // Generic export handler function
  const handleExport = async (type) => {
    try {
      let amazonShipment = [];
      let otherShipment = selectedRows.map((row) => row.shipmentId);
      let olderShipment = [];
      if (type.toLowerCase() === "labels") {
        otherShipment = [];
        selectedRows.forEach((row) => {
          if (
            courier.find((c) => c._id === row.courierId)?.name ===
            "Amazon Shipping"
          ) {
            const createdAt = new Date(row.createdAt);
            const now = new Date();
            const diffMs = now - createdAt;
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            if (diffDays <= 30) {
              amazonShipment.push(row?.key);
            } else {
              olderShipment.push(row?.shipmentId);
            }
          } else {
            otherShipment.push(row.shipmentId);
          }
        });
        if (amazonShipment.length > 0) {
          downloadFile(
            "/user/export/amazonLabels",
            amazonShipment,
            "labels",
            true,
          );
        }
        if (olderShipment.length > 0) {
        }
      }

      if (olderShipment.length <= 0 && otherShipment.length > 0)
        downloadFile(`/user/export/${type}`, otherShipment, type, false);
      if (olderShipment.length > 0) {
        alert(
          "Only shipments less than 30 days can be exported.\n" +
            olderShipment.join(",\n"),
        );
      }
    } catch (error) {
      console.error("Export error:", error);
      alert(`Failed to download ${type}. Please try again.`);
    }
  };

  const handleCancel = async () => {
    const shippingIds = selectedRows.map((row) => row.shipmentId);
    console.log("Cancelling Shipments:", shippingIds);
    if (shippingIds.length === 0) {
      alert("Please select at least one shipment to cancel.");
      return;
    }
    await confirm("Are you sure you want to cancel the selected shipments?");
    try {
      const action = await dispatch(cancelShipment(shippingIds));
      if (cancelShipment.fulfilled.match(action)) {
        await alert("Shipments Cancelled Successfully", "success");
        setSelectedRows([]); // Clear selected rows after cancellation
      } else if (cancelShipment.rejected.match(action)) {
        await alert("Shipment Cancellation Failed", "error");
      }
    } catch (error) {
      console.error("Shipment Cancellation Failed:", error);
    }
  };
  // Update the bulk actions handler
  const handleOptionClick = (value) => {
    console.log("Option Selected:", value);
    switch (value) {
      case "bulk_label":
        handleExport("Labels");
        break;
      case "bulk_invoice":
        handleExport("Invoice");
        break;
      case "manifest":
        handleExport("Manifest");
        break;
      case "cancel":
        handleCancel();
        break;
      default:
        break;
    }
  };

  const handleDateChange = useCallback(
    (dateRange) => {
      console.log("Date changed:", dateRange);
      setFilters({
        date: {
          from: dateRange?.from || startOfMonthDate,
          to: dateRange?.to || endOfMonthDate,
        },
      });
    },
    [setFilters, startOfMonthDate, endOfMonthDate],
  );

  /* ----------------------- Render ----------------------- */
  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="w-full flex flex-col gap-2 md:flex-row justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            Track Shipments
          </h2>
          {
            <div className="flex gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate("/settings/labelSettings")}
                icon={BiSolidPrinter}
              >
                Print Settings
              </Button>
            </div>
          }
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            mode="range"
            placeholder="Select From & To Date"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Enter Order ID"
            value={filters.orderId}
            onChange={(value) => handleFilterChange("orderId", value)}
          />
          <DropDownInput
            label="Courier Name"
            placeholder="Select Courier Name"
            options={courierOptions}
            value={filters.courierName}
            onChange={(value) => handleFilterChange("courierName", value)}
          />
          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={channelOptions}
            value={filters.channelName}
            onChange={(value) => handleFilterChange("channelName", value)}
          />
        </div>

        {/* Additional Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
          <InputField
            label="AWBs"
            placeholder=" AWBs separated by commas"
            value={filters.awbs}
            onChange={(value) => handleFilterChange("awbs", value)}
          />
          <InputField
            label="Shipping ID"
            placeholder=" Shipping Id separated by commas"
            value={filters.shippingId}
            onChange={(value) => handleFilterChange("shippingId", value)}
          />
          <InputField
            label="Customer Name"
            placeholder=" Customer Name separated by commas"
            value={filters.customerName}
            onChange={(value) => handleFilterChange("customerName", value)}
          />
          <InputField
            label="Customer Phone"
            placeholder=" Customer Phone separated by commas"
            value={filters.customerPhone}
            onChange={(value) => handleFilterChange("customerPhone", value)}
          />
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={paymentOptions}
            selectedValue={filters.paymentMode}
            onChange={(value) => handleFilterChange("paymentMode", value)}
          />
          <RadioInput
            label="Service Mode"
            name="service_mode"
            options={serviceMode}
            selectedValue={filters.serviceMode}
            onChange={(value) => handleFilterChange("serviceMode", value)}
          />

          <div className="flex flex-row gap-2 justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={applyFilters}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={resetFilters}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <MiniTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          showMore={true}
        />

        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <OptionGroup
            options={options}
            selectedCount={selectedRows.length}
            onClick={handleOptionClick}
          />
        )}

        {/* DataTable */}
        <DataTable
          columns={columns}
          tittle="Shipments"
          data={finalFilteredData}
          getSelectedRows={setSelectedRows}
          isLoading={isLoading}
          resetPage={resetPageCounter}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Tracking",
                filename: `Shipments${new Date().getTime()}`,
              },
            },
          ]}
          isRowSelectable={(rowData) => {
            if (
              rowData.status === "Delivered" ||
              rowData.status === "Cancelled"
            ) {
              return false;
            }
            return true;
          }}
        />
      </div>
      <AlertDialog />
      <ConfirmationModal />
    </SpaceWrapper>
  );
};

export default Tracking;
